import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { handleApiError } from '../lib/api';
import { Agent } from '@ouagaliv/shared';
import {
  FileText, GraduationCap, ShieldCheck, CheckCircle2, Circle,
  AlertCircle, ArrowLeft, Check,
} from 'lucide-react';

const TRAINING_TOPICS = [
  { id: 'safety', title: 'Sécurité routière', text: 'Respecter le code de la route, porter un casque à moto et rouler prudemment avec un colis.' },
  { id: 'client', title: 'Relation client', text: 'Être poli, ponctuel, et prévenir le client en cas de retard.' },
  { id: 'delivery', title: 'Procédure de livraison', text: 'Vérifier le colis au retrait, le protéger pendant le trajet, le remettre en main propre.' },
  { id: 'app', title: 'Utilisation de l\u2019application', text: 'Accepter une course en moins de 15 secondes, suivre les statuts étape par étape, activer sa disponibilité pour recevoir des courses.' },
];

export default function Onboarding() {
  const { user, updateProfile, completeTraining, refreshUser } = useAuth();
  const agentUser = user as Agent | null;

  const [idDocumentUrl, setIdDocumentUrl] = useState(agentUser?.idDocumentUrl || '');
  const [licenseNumber, setLicenseNumber] = useState(agentUser?.licenseNumber || '');
  const [licenseExpiry, setLicenseExpiry] = useState(
    agentUser?.licenseExpiry ? agentUser.licenseExpiry.slice(0, 10) : ''
  );
  const [savingDocs, setSavingDocs] = useState(false);
  const [docsError, setDocsError] = useState('');
  const [docsSaved, setDocsSaved] = useState(false);

  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [completingTraining, setCompletingTraining] = useState(false);
  const [trainingError, setTrainingError] = useState('');

  const hasDocuments = Boolean(agentUser?.idDocumentUrl);
  const trainingDone = Boolean(agentUser?.trainingCompleted);
  const isActive = agentUser?.status === 'active';
  const allChecked = TRAINING_TOPICS.every(t => checked[t.id]);

  const handleSaveDocuments = async (e: React.FormEvent) => {
    e.preventDefault();
    setDocsError('');
    setDocsSaved(false);
    setSavingDocs(true);
    try {
      await updateProfile({
        idDocumentUrl: idDocumentUrl || undefined,
        licenseNumber: licenseNumber || undefined,
        licenseExpiry: licenseExpiry ? new Date(licenseExpiry).toISOString() : undefined,
      } as any);
      await refreshUser();
      setDocsSaved(true);
    } catch (err) {
      setDocsError(handleApiError(err));
    } finally {
      setSavingDocs(false);
    }
  };

  const handleCompleteTraining = async () => {
    setTrainingError('');
    setCompletingTraining(true);
    try {
      await completeTraining();
    } catch (err) {
      setTrainingError(handleApiError(err));
    } finally {
      setCompletingTraining(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container pt-4 pb-24">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/profile" aria-label="Retour au profil" className="p-2 rounded-lg hover:bg-gray-100 text-text">
            <ArrowLeft size={22} />
          </Link>
          <h1 className="text-xl font-semibold text-text">Activer mon compte</h1>
        </div>

        <div className={`card mb-4 flex items-center gap-3 ${isActive ? '' : ''}`} style={{ borderColor: isActive ? '#059669' : undefined }}>
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isActive ? 'bg-green-100 text-green-600' : 'bg-amber-100 text-amber-700'}`}>
            <ShieldCheck size={24} />
          </div>
          <div>
            <p className="font-semibold text-text">{isActive ? 'Compte activé' : 'Compte en attente d\u2019activation'}</p>
            <p className="text-sm text-text-secondary">
              {isActive
                ? 'Vous pouvez passer disponible et recevoir des courses.'
                : 'Complétez les 2 étapes ci-dessous pour activer votre compte.'}
            </p>
          </div>
        </div>

        <div className="card mb-4">
          <div className="flex items-center gap-2 mb-3">
            {hasDocuments ? <CheckCircle2 size={20} className="text-green-600" /> : <Circle size={20} className="text-text-secondary" />}
            <h2 className="font-semibold text-text flex items-center gap-2"><FileText size={18} /> Mes informations & documents</h2>
          </div>

          {docsError && (
            <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
              <AlertCircle size={16} /> {docsError}
            </div>
          )}
          {docsSaved && (
            <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
              <Check size={16} /> Documents enregistrés
            </div>
          )}

          <form onSubmit={handleSaveDocuments} className="space-y-4">
            <div className="input-group">
              <label htmlFor="idDocumentUrl" className="input-label">Pièce d'identité (lien vers une photo ou un scan)</label>
              <input
                id="idDocumentUrl"
                type="url"
                className="input-field"
                value={idDocumentUrl}
                onChange={e => setIdDocumentUrl(e.target.value)}
                placeholder="https://..."
                required
              />
              <p className="input-hint">CNI, passeport ou carte consulaire.</p>
            </div>
            <div className="input-group">
              <label htmlFor="licenseNumber" className="input-label">Numéro de permis (si véhicule motorisé)</label>
              <input
                id="licenseNumber"
                type="text"
                className="input-field"
                value={licenseNumber}
                onChange={e => setLicenseNumber(e.target.value)}
                placeholder="PERMIS-12345"
              />
            </div>
            <div className="input-group">
              <label htmlFor="licenseExpiry" className="input-label">Date d'expiration du permis</label>
              <input
                id="licenseExpiry"
                type="date"
                className="input-field"
                value={licenseExpiry}
                onChange={e => setLicenseExpiry(e.target.value)}
              />
            </div>
            <button type="submit" className="btn btn-primary btn-full" disabled={savingDocs}>
              {savingDocs ? 'Enregistrement...' : 'Enregistrer mes documents'}
            </button>
          </form>
        </div>

        <div className="card mb-4">
          <div className="flex items-center gap-2 mb-3">
            {trainingDone ? <CheckCircle2 size={20} className="text-green-600" /> : <Circle size={20} className="text-text-secondary" />}
            <h2 className="font-semibold text-text flex items-center gap-2"><GraduationCap size={18} /> Formation obligatoire</h2>
          </div>

          {trainingError && (
            <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
              <AlertCircle size={16} /> {trainingError}
            </div>
          )}

          {trainingDone ? (
            <p className="text-sm text-text-secondary">Formation terminée{agentUser?.trainingCompletedAt ? ` le ${new Date(agentUser.trainingCompletedAt).toLocaleDateString('fr-FR')}` : ''}.</p>
          ) : (
            <>
              <div className="space-y-3 mb-4">
                {TRAINING_TOPICS.map(t => (
                  <label key={t.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={!!checked[t.id]}
                      onChange={e => setChecked(prev => ({ ...prev, [t.id]: e.target.checked }))}
                    />
                    <div>
                      <p className="font-medium text-text text-sm">{t.title}</p>
                      <p className="text-xs text-text-secondary">{t.text}</p>
                    </div>
                  </label>
                ))}
              </div>
              <button
                type="button"
                onClick={handleCompleteTraining}
                className="btn btn-primary btn-full"
                disabled={!allChecked || completingTraining}
              >
                {completingTraining ? 'Validation...' : 'Terminer la formation'}
              </button>
              {!allChecked && (
                <p className="input-hint mt-2">Cochez chaque point pour valider la formation.</p>
              )}
            </>
          )}
        </div>

        {!isActive && (
          <p className="text-xs text-text-secondary text-center">
            L'activation est automatique dès que vos documents sont enregistrés et la formation terminée.
          </p>
        )}
      </div>
    </div>
  );
}
